import * as React from 'react';

import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogOverlay,
  DialogMain,
} from '@rematter/ui-web';
import { Button } from '@rematter/ui-web';
import { RadioGroupField, Radio } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { useCreateRouteMutation } from '../../hooks/mutations/useCreateRouteMutation';
import { useUpdateRouteMutation } from '../../hooks/mutations/useUpdateRouteMutation';
import {
  $resetRouteForm,
  $routeForm,
  $updateRouteForm,
} from '../../state/routeForm.localState';
import { RouteFormState } from '../../types';

import { RouteForm } from './RouteForm';
import { RouteStatusActions } from './RouteStatusActions';
import { RouteTasksList } from './RouteTasksList';

import { $organization } from '~/shared/providers/auth/identity.localState';

type RouteDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data?: { routeId: string; serviceIds: string[] }) => void;
  route?: RouteFormState & { id: string };
};

export const RouteDialog = ({
  isOpen,
  onClose,
  onSuccess,
  route,
}: RouteDialogProps) => {
  const t = useTranslations();
  const [isViewMode, setIsViewMode] = React.useState(false);
  const [tab, setTab] = React.useState<'services' | 'tasks' | 'routeMap'>(
    'services',
  );
  const form = useAtomValue($routeForm);
  const organization = useAtomValue($organization);
  const updateForm = useSetAtom($updateRouteForm);
  const resetForm = useSetAtom($resetRouteForm);
  const toast = useToast();

  const { mutateAsync: createRoute, isPending: isCreating } =
    useCreateRouteMutation();
  const { mutateAsync: updateRoute, isPending: isUpdating } =
    useUpdateRouteMutation();

  React.useEffect(() => {
    if (route) {
      updateForm(route);
    } else if (organization) {
      updateForm({ haulerId: organization.id });
    }
    setIsViewMode(!!route);
  }, [route, updateForm, organization]);

  const handleClose = () => {
    onClose();
    setIsViewMode(false);
    resetForm();
  };

  const handleSave = async () => {
    try {
      if (!form.facilityGroupId) {
        throw new Error(t.routesView.form.messages.facilityGroupRequired);
      }

      const formData = {
        ...form,
        facilityGroupId: form.facilityGroupId,
      };

      if (route) {
        await updateRoute({
          id: route.id,
          ...formData,
        });
        toast.success(t.routesView.form.messages.updateSuccess);
        setIsViewMode(true);
        onSuccess({
          routeId: route.id,
          serviceIds: formData.serviceIds ?? [],
        });
      } else {
        await createRoute(formData);
        toast.success(t.routesView.form.messages.createSuccess);
        onSuccess();
      }
      handleClose();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t.routesView.form.messages.error,
      );
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={handleClose}
      isDismissable={false}
      className="@RouteDialog"
    >
      <DialogContent className="max-w-4xl min-w-[600px]">
        <DialogHeader slot="title">
          <div className="flex flex-col gap-2">
            <div className="text-xl font-semibold">
              {route
                ? t.routesView.form.title.view
                : t.routesView.form.title.create}
            </div>
            {route && isViewMode && (
              <Button
                onPress={() => setIsViewMode(false)}
                variant="link"
                className="h-auto w-fit p-0 text-sm text-primary-darker hover:text-primary/70"
              >
                {t.routesView.form.buttons.edit}
              </Button>
            )}
          </div>
        </DialogHeader>

        <DialogMain>
          <div className="flex gap-8">
            <div className="w-1/3" />
            <div className="mb-4 flex w-2/3 justify-center">
              <RadioGroupField
                value={tab}
                onChange={(val) =>
                  setTab(val as 'services' | 'tasks' | 'routeMap')
                }
                orientation="horizontal"
              >
                <Radio value="services">
                  {t.routesView.form.tabs.services}
                </Radio>
                {route && (
                  <Radio value="tasks">{t.routesView.form.tabs.tasks}</Radio>
                )}
                <Radio value="routeMap">
                  {t.routesView.form.tabs.routeMap}
                </Radio>
              </RadioGroupField>
            </div>
          </div>
          {tab === 'tasks' && route ? (
            <div className="flex max-h-[380px] gap-8">
              <div className="w-1/3" />
              <div className="w-2/3">
                <RouteTasksList routeId={route.id} />
              </div>
            </div>
          ) : (
            <RouteForm
              isViewMode={isViewMode}
              activeTab={tab === 'tasks' ? 'services' : tab}
            />
          )}
        </DialogMain>

        <DialogFooter>
          {isViewMode && route ? (
            <RouteStatusActions
              route={route}
              onSuccess={() => onSuccess()}
              onClose={handleClose}
            />
          ) : (
            // Edit/Create mode actions
            <>
              <Button
                color="failure"
                variant="outline"
                isPending={isLoading}
                onPress={handleClose}
                isLoading={isLoading}
              >
                {t.routesView.form.buttons.cancel}
              </Button>
              <Button
                color="primary"
                variant="solid"
                isPending={isLoading}
                onPress={handleSave}
                isLoading={isLoading}
              >
                {route
                  ? t.routesView.form.buttons.save
                  : t.routesView.form.buttons.create}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
